import {
  invoiceImportActionNames,
  invoiceImportDirections,
  invoiceImportDirectionSources,
  invoiceImportFa3CorrectionVariants,
  invoiceImportFa3Dispositions,
  invoiceImportFa3Families,
  invoiceImportFa3LineRoles,
  invoiceImportFa3LineSources,
  invoiceImportMatchedSubjectRoles,
  invoiceImportFa3RelationTypes,
  invoiceImportFa3SupportStatuses,
  InvoiceImportIssueSeverity,
  type ImportedInvoiceDataDto,
  type ImportedInvoiceItemDto,
  type ImportedInvoiceParserMetadataDto,
  type InvoiceImportFa3DocumentLinkDto,
  type InvoiceImportFa3SemanticPreviewDto,
  type InvoiceImportFa3SemanticPreviewLineDto,
  type InvoiceImportHistoryDto,
  type InvoiceImportIssueDto,
  type InvoiceImportMatchedSubjectRole,
  type InvoiceImportStatusDto,
  type InvoiceImportDirection,
  type InvoiceImportDirectionSource,
  type PagedListDto,
} from '../Types/invoiceImport.types';
import { getImportIssueMessage } from '../Utils/invoiceImportMessages';

const normalizeString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeRequiredString = (value: string | null | undefined): string =>
  normalizeString(value) ?? '';

const isOneOf = <T extends readonly string[]>(
  value: string | null | undefined,
  allowed: T
): value is T[number] => typeof value === 'string' && allowed.includes(value as T[number]);

const normalizeImportedInvoiceParserMetadata = (
  metadata: ImportedInvoiceParserMetadataDto | null | undefined
): ImportedInvoiceParserMetadataDto | null => {
  if (!metadata) {
    return null;
  }

  const overlayFlags = (metadata.overlayFlags ?? [])
    .map(normalizeString)
    .filter((flag): flag is string => flag !== null);

  const provenance = Object.entries(metadata.provenance ?? {}).reduce<Record<string, string>>(
    (accumulator, [key, value]) => {
      const normalizedKey = normalizeString(key);
      const normalizedValue = normalizeString(value);

      if (normalizedKey && normalizedValue) {
        accumulator[normalizedKey] = normalizedValue;
      }

      return accumulator;
    },
    {}
  );

  return {
    sourceDocumentKind: normalizeString(metadata.sourceDocumentKind),
    documentFamily: normalizeString(metadata.documentFamily),
    lineSource: normalizeString(metadata.lineSource),
    correctionVariant: normalizeString(metadata.correctionVariant),
    correctionReason: normalizeString(metadata.correctionReason),
    sourceKsefNumber: normalizeString(metadata.sourceKsefNumber),
    overlayFlags,
    provenance,
  };
};

const normalizeImportedInvoiceItem = (
  item: ImportedInvoiceItemDto | null | undefined
): ImportedInvoiceItemDto | null => {
  if (!item) {
    return null;
  }

  return {
    ...item,
    name: normalizeString(item.name),
    description: normalizeString(item.description),
    vatType: normalizeString(item.vatType),
    vatExemptNote: normalizeString(item.vatExemptNote),
    sourceSection: normalizeString(item.sourceSection),
    sourceLineNumber:
      typeof item.sourceLineNumber === 'number' ? item.sourceLineNumber : null,
    isBeforeCorrectionState:
      typeof item.isBeforeCorrectionState === 'boolean' ? item.isBeforeCorrectionState : null,
  };
};

const normalizeImportedInvoiceData = (
  parsedInvoiceData: ImportedInvoiceDataDto | null | undefined
): ImportedInvoiceDataDto | null => {
  if (!parsedInvoiceData) {
    return null;
  }

  return {
    ...parsedInvoiceData,
    invoiceNumber: normalizeString(parsedInvoiceData.invoiceNumber),
    issueDate: normalizeString(parsedInvoiceData.issueDate),
    saleDate: normalizeString(parsedInvoiceData.saleDate),
    placeOfIssue: normalizeString(parsedInvoiceData.placeOfIssue),
    type: normalizeString(parsedInvoiceData.type),
    scheme: normalizeString(parsedInvoiceData.scheme),
    importerType: normalizeString(parsedInvoiceData.importerType),
    sellerName: normalizeString(parsedInvoiceData.sellerName),
    sellerAddress: normalizeString(parsedInvoiceData.sellerAddress),
    sellerNip: normalizeString(parsedInvoiceData.sellerNip),
    sellerBankAccount: normalizeString(parsedInvoiceData.sellerBankAccount),
    buyerName: normalizeString(parsedInvoiceData.buyerName),
    buyerAddress: normalizeString(parsedInvoiceData.buyerAddress),
    buyerNip: normalizeString(parsedInvoiceData.buyerNip),
    recipientName: normalizeString(parsedInvoiceData.recipientName),
    recipientAddress: normalizeString(parsedInvoiceData.recipientAddress),
    recipientNip: normalizeString(parsedInvoiceData.recipientNip),
    currency: normalizeString(parsedInvoiceData.currency)?.toUpperCase() ?? null,
    paymentMethod: normalizeString(parsedInvoiceData.paymentMethod),
    paymentDueDate: normalizeString(parsedInvoiceData.paymentDueDate),
    paidAt: normalizeString(parsedInvoiceData.paidAt),
    paymentComment: normalizeString(parsedInvoiceData.paymentComment),
    comments: normalizeString(parsedInvoiceData.comments),
    legalNote: normalizeString(parsedInvoiceData.legalNote),
    supplierName: normalizeString(parsedInvoiceData.supplierName),
    supplierTaxId: normalizeString(parsedInvoiceData.supplierTaxId),
    supplierAddress: normalizeString(parsedInvoiceData.supplierAddress),
    supplierBankAccount: normalizeString(parsedInvoiceData.supplierBankAccount),
    items: (parsedInvoiceData.items ?? [])
      .map(normalizeImportedInvoiceItem)
      .filter((item): item is ImportedInvoiceItemDto => item !== null),
    parserMetadata: normalizeImportedInvoiceParserMetadata(parsedInvoiceData.parserMetadata),
  };
};

const normalizeInvoiceImportFa3DocumentLink = (
  link: InvoiceImportFa3DocumentLinkDto | null | undefined
): InvoiceImportFa3DocumentLinkDto | null => {
  if (!link || !isOneOf(link.relationType, invoiceImportFa3RelationTypes)) {
    return null;
  }

  return {
    relationType: link.relationType,
    relatedExpenseInvoiceId:
      typeof link.relatedExpenseInvoiceId === 'number' ? link.relatedExpenseInvoiceId : null,
    relatedDocumentReference: normalizeString(link.relatedDocumentReference),
    externalInvoiceNumber: normalizeString(link.externalInvoiceNumber),
    externalKsefNumber: normalizeString(link.externalKsefNumber),
    externalIssueDate: normalizeString(link.externalIssueDate),
    isResolved: Boolean(link.isResolved),
  };
};

const normalizeInvoiceImportFa3SemanticPreviewLine = (
  line: InvoiceImportFa3SemanticPreviewLineDto | null | undefined
): InvoiceImportFa3SemanticPreviewLineDto | null => {
  if (!line || !isOneOf(line.lineRole, invoiceImportFa3LineRoles)) {
    return null;
  }

  return {
    lineRole: line.lineRole,
    sourceSection: normalizeRequiredString(line.sourceSection),
    sourceLineNumber: typeof line.sourceLineNumber === 'number' ? line.sourceLineNumber : null,
  };
};

const normalizeInvoiceImportFa3SemanticPreview = (
  preview: InvoiceImportFa3SemanticPreviewDto | null | undefined
): InvoiceImportFa3SemanticPreviewDto | null => {
  if (!preview) {
    return null;
  }

  if (
    !isOneOf(preview.family, invoiceImportFa3Families) ||
    !isOneOf(preview.disposition, invoiceImportFa3Dispositions) ||
    !isOneOf(preview.supportStatus, invoiceImportFa3SupportStatuses) ||
    !isOneOf(preview.lineSource, invoiceImportFa3LineSources)
  ) {
    return null;
  }

  return {
    family: preview.family,
    disposition: preview.disposition,
    supportStatus: preview.supportStatus,
    lineSource: preview.lineSource,
    correctionVariant: isOneOf(preview.correctionVariant, invoiceImportFa3CorrectionVariants)
      ? preview.correctionVariant
      : null,
    correctionReason: normalizeString(preview.correctionReason),
    unsupportedReason: normalizeString(preview.unsupportedReason),
    links: (preview.links ?? [])
      .map(normalizeInvoiceImportFa3DocumentLink)
      .filter((link): link is InvoiceImportFa3DocumentLinkDto => link !== null),
    lines: (preview.lines ?? [])
      .map(normalizeInvoiceImportFa3SemanticPreviewLine)
      .filter((line): line is InvoiceImportFa3SemanticPreviewLineDto => line !== null),
  };
};

const normalizeInvoiceDirection = (value: string | null | undefined): InvoiceImportDirection =>
  (() => {
    const normalized = normalizeString(value);
    return normalized && isOneOf(normalized, invoiceImportDirections) ? normalized : 'unknown';
  })();

const normalizeInvoiceDirectionSource = (
  value: string | null | undefined
): InvoiceImportDirectionSource =>
  (() => {
    const normalized = normalizeString(value);
    return normalized && isOneOf(normalized, invoiceImportDirectionSources)
      ? normalized
      : 'derivedRuntime';
  })();

const normalizeMatchedSubjectRole = (
  value: string | null | undefined
): InvoiceImportMatchedSubjectRole | null =>
  (() => {
    const normalized = normalizeString(value);
    return normalized && isOneOf(normalized, invoiceImportMatchedSubjectRoles) ? normalized : null;
  })();

const issueSeverities = Object.values(InvoiceImportIssueSeverity);

/**
 * Confidence must stay on the backend's 0..1 scale (RM-06: this scale has drifted before). A
 * value outside that range is treated as unknown rather than silently rendered as a nonsensical
 * percentage.
 */
const normalizeOcrConfidence = (value: number | null | undefined): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0 || value > 1) {
    return null;
  }

  return value;
};

const normalizeIssueParameters = (
  parameters: Record<string, string> | null | undefined
): Record<string, string> | null => {
  if (!parameters) {
    return null;
  }

  const entries = Object.entries(parameters).reduce<Record<string, string>>(
    (accumulator, [key, value]) => {
      const normalizedKey = normalizeString(key);
      const normalizedValue = normalizeString(value);

      if (normalizedKey && normalizedValue) {
        accumulator[normalizedKey] = normalizedValue;
      }

      return accumulator;
    },
    {}
  );

  return Object.keys(entries).length > 0 ? entries : null;
};

const normalizeInvoiceImportIssue = (
  issue: InvoiceImportIssueDto
): InvoiceImportIssueDto => ({
  code: normalizeRequiredString(issue.code),
  // Never trust raw backend/provider text directly (RM-07): always resolve through the local
  // code-keyed allowlist.
  message: getImportIssueMessage(issue.code),
  field: normalizeString(issue.field),
  blocksAction: Boolean(issue.blocksAction),
  severity: isOneOf(issue.severity, issueSeverities)
    ? issue.severity
    : InvoiceImportIssueSeverity.Error,
  parameters: normalizeIssueParameters(issue.parameters),
  retryable: Boolean(issue.retryable),
});

const normalizeInvoiceImportIssues = (
  issues: InvoiceImportIssueDto[] | null | undefined
): InvoiceImportIssueDto[] => (issues ?? []).map(normalizeInvoiceImportIssue);

/** Renders only from the backend contract (RM-09): never re-derives eligibility itself. */
const normalizeAllowedActions = (actions: string[] | null | undefined): string[] =>
  (actions ?? []).filter((action) => isOneOf(action, invoiceImportActionNames));

export const normalizeImportStatusResponse = (
  response: InvoiceImportStatusDto
): InvoiceImportStatusDto => ({
  ...response,
  fileName: normalizeRequiredString(response.fileName),
  contentType: normalizeRequiredString(response.contentType),
  blobStoragePath: normalizeRequiredString(response.blobStoragePath),
  importerType: normalizeString(response.importerType),
  invoiceNumber: normalizeString(response.invoiceNumber),
  expenseInvoiceId:
    typeof response.expenseInvoiceId === 'number' ? response.expenseInvoiceId : null,
  ocrConfidence: normalizeOcrConfidence(response.ocrConfidence),
  userId: typeof response.userId === 'number' ? response.userId : null,
  attemptNumber: typeof response.attemptNumber === 'number' ? response.attemptNumber : 0,
  rootImportJobId:
    typeof response.rootImportJobId === 'number' ? response.rootImportJobId : response.importJobId,
  previousAttemptImportJobId:
    typeof response.previousAttemptImportJobId === 'number'
      ? response.previousAttemptImportJobId
      : null,
  duplicateOfImportJobId:
    typeof response.duplicateOfImportJobId === 'number' ? response.duplicateOfImportJobId : null,
  isRetryable: Boolean(response.isRetryable),
  issues: normalizeInvoiceImportIssues(response.issues),
  allowedActions: normalizeAllowedActions(response.allowedActions),
  warnings: (response.warnings ?? [])
    .map(normalizeString)
    .filter((warning): warning is string => warning !== null),
  errorMessage: normalizeString(response.errorMessage),
  rejectionReason: normalizeString(response.rejectionReason),
  rejectedAt: normalizeString(response.rejectedAt),
  approvedAt: normalizeString(response.approvedAt),
  invoiceDirection: normalizeInvoiceDirection(response.invoiceDirection),
  invoiceDirectionReason: normalizeString(response.invoiceDirectionReason),
  matchedSubjectRole: normalizeMatchedSubjectRole(response.matchedSubjectRole),
  invoiceDirectionSource: normalizeInvoiceDirectionSource(response.invoiceDirectionSource),
  parsedInvoiceData: normalizeImportedInvoiceData(response.parsedInvoiceData),
  fa3SemanticPreview: normalizeInvoiceImportFa3SemanticPreview(response.fa3SemanticPreview),
});

const normalizeImportHistoryItem = (item: InvoiceImportHistoryDto): InvoiceImportHistoryDto => ({
  ...item,
  fileName: normalizeString(item.fileName) ?? '—',
  statusLabel: normalizeString(item.statusLabel) ?? 'Nieznany status',
  invoiceNumber: normalizeString(item.invoiceNumber),
  invoiceDirection: normalizeInvoiceDirection(item.invoiceDirection),
  invoiceDirectionReason: normalizeString(item.invoiceDirectionReason),
  matchedSubjectRole: normalizeMatchedSubjectRole(item.matchedSubjectRole),
  invoiceDirectionSource: normalizeInvoiceDirectionSource(item.invoiceDirectionSource),
  userId: typeof item.userId === 'number' ? item.userId : null,
  attemptNumber: typeof item.attemptNumber === 'number' ? item.attemptNumber : 0,
  rootImportJobId: typeof item.rootImportJobId === 'number' ? item.rootImportJobId : item.importJobId,
  previousAttemptImportJobId:
    typeof item.previousAttemptImportJobId === 'number' ? item.previousAttemptImportJobId : null,
  duplicateOfImportJobId:
    typeof item.duplicateOfImportJobId === 'number' ? item.duplicateOfImportJobId : null,
  isRetryable: Boolean(item.isRetryable),
  issues: normalizeInvoiceImportIssues(item.issues),
  allowedActions: normalizeAllowedActions(item.allowedActions),
});

export const normalizeImportHistoryResponse = (
  response: PagedListDto<InvoiceImportHistoryDto>
): PagedListDto<InvoiceImportHistoryDto> => ({
  ...response,
  items: (response.items ?? []).map(normalizeImportHistoryItem),
});
